import { useEffect, useState } from "react";
import { Avatar, Button, Card, Chip, Stack, Typography } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Getrequestcall } from "../../request/Getrequest";
import { Postrequestcall } from "../../request/Postrequest";
import Loader from "../../common/Loader";
import {
  FETCH_CLIENT_COMMUNICATOR_TAGS_BY_EMPLOYEES,
  GET_CLIENT_COMMUNICATOR_DETAIL
} from "../../constant/Apipath";
import ClientCommunicatorViewModal from "./ClientCommunicatorViewModal";
import ClientCommunicatorFilterModal from "./ClientCommunicatorFilterModal";
import TabsComponent from "../component/TabsComponent";
import { ClientEnglishFallback } from "../constant/ClientEnglishFallback";

const asArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (value && typeof value === "object") {
    return [value];
  }
  return [];
};

const extractTagPayload = (responseData) => {
  const possibleSources = [
    responseData?.data,
    responseData?.result,
    responseData?.items,
    responseData?.records,
    responseData?.list,
    responseData?.tags,
    responseData?.tagList,
    responseData
  ];

  for (const source of possibleSources) {
    const items = asArray(source);
    if (items.length > 0) {
      return items;
    }
  }

  return [];
};

const normalizeTagLabel = (tag, value) => {
  return (
    tag?.tagName ??
    tag?.TagName ??
    tag?.tagNames ??
    tag?.TagNames ??
    tag?.name ??
    tag?.Name ??
    tag?.label ??
    tag?.Label ??
    tag?.description ??
    tag?.Description ??
    tag?.title ??
    tag?.Title ??
    (value !== undefined && value !== null ? String(value) : "")
  );
};

const normalizeTagValue = (tag) => {
  return (
    tag?.tagId ??
    tag?.TagId ??
    tag?.id ??
    tag?.Id ??
    tag?.clientCommunicatorTagId ??
    tag?.ClientCommunicatorTagId ??
    tag?.tagIds ??
    tag?.TagIds ??
    null
  );
};

function Communicator() {
  const location = useLocation();
  const dataId = location.state?.id;
  const navigate = useNavigate();

  const { loginData, selectedClientData, multiLanguageData } = useSelector((state) => state?.main);
  const LANGUAGE = ClientEnglishFallback.ClientCommunicator(
    multiLanguageData?.ClientCommunicator || {}
  );
  const { TEXT_ALL, TEXT_TO_ACKNOWLEDGE, TEXT_ACKNOWLEDGED, TEXT_NO_MASSAGES } = LANGUAGE;
  const tagLanguage = LANGUAGE;

  const [communicatorData, setCommunicatorData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTagFilterLoading, setIsTagFilterLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [viewCommunicator, setViewCommunicator] = useState(false);
  const [shareData, setShareData] = useState([]);
  const [employeeTags, setEmployeeTags] = useState([]);
  const [selectedTagId, setSelectedTagId] = useState(0);
  const [tagFilterOpen, setTagFilterOpen] = useState(false);
  const [tagFilterAutoOpened, setTagFilterAutoOpened] = useState(false);
  const [tagLookupLoaded, setTagLookupLoaded] = useState(false);

  useEffect(() => {
    setEmployeeTags([]);
    setSelectedTagId(0);
    setTagFilterOpen(false);
    setTagFilterAutoOpened(false);
    setTagLookupLoaded(false);
  }, [selectedClientData?.ClientEmployeeId, selectedClientData?.ClientId]);

  useEffect(() => {
    const loadCommunicator = async () => {
      if (
        !selectedClientData?.ClientEmployeeId ||
        !selectedClientData?.ClientId ||
        !loginData?.accessToken
      ) {
        setCommunicatorData([]);
        setIsLoading(false);
        return;
      }

      setIsTagFilterLoading(true);
      try {
        const tagResponse = await Postrequestcall(
          FETCH_CLIENT_COMMUNICATOR_TAGS_BY_EMPLOYEES,
          {
            ClientEmployeeId: selectedClientData?.ClientEmployeeId,
            ClientId: selectedClientData?.ClientId
          },
          loginData?.accessToken
        );
        const tagList = extractTagPayload(tagResponse?.data);

        const normalizedTags = tagList
          .flatMap((tag) => {
            if (tag === null || tag === undefined) {
              return [];
            }

            if (typeof tag === "string" || typeof tag === "number") {
              return [
                {
                  value: tag,
                  label: String(tag)
                }
              ];
            }

            const value = normalizeTagValue(tag);
            const label = normalizeTagLabel(tag, value);

            if (typeof value === "string" && value.includes(",") && !label) {
              return value
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean)
                .map((item) => ({
                  value: item,
                  label: item
                }));
            }

            if (value === null || value === undefined || value === "") {
              return label
                ? [
                    {
                      value: label,
                      label
                    }
                  ]
                : [];
            }

            return [
              {
                value,
                label
              }
            ];
          })
          .filter((tag) => tag.value !== undefined && tag.value !== null && tag.label);

        setEmployeeTags(normalizedTags);
        setSelectedTagId((prev) => {
          if (normalizedTags.length === 0) {
            return 0;
          }
          const isStillValid = normalizedTags.some((tag) => tag.value === prev);
          return isStillValid ? prev : 0;
        });

        if (normalizedTags.length > 0 && !tagFilterAutoOpened) {
          setTagFilterOpen(true);
          setTagFilterAutoOpened(true);
        } else if (normalizedTags.length === 0) {
          setTagFilterOpen(false);
        }
        setTagLookupLoaded(true);
      } catch (error) {
        setEmployeeTags([]);
        setSelectedTagId(0);
        setTagFilterOpen(false);
        setTagLookupLoaded(true);
        console.log("loadCommunicator-> Error Getting Communicator Tags: ", error);
      } finally {
        setIsTagFilterLoading(false);
      }
    };

    loadCommunicator();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientData?.ClientEmployeeId, selectedClientData?.ClientId, loginData?.accessToken]);

  useEffect(() => {
    if (typeof dataId === "number") {
      getAndSet(dataId);
    } else {
      getCommunicatorData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, location, selectedTagId, selectedClientData?.ClientEmployeeId, loginData?.accessToken]);

  const getAndSet = async () => {
    await getCommunicatorData(dataId);
    setViewCommunicator(true);
  };

  const getCommunicatorData = async (dataId) => {
    setIsLoading(true);
    try {
      const communicatorResponse = await Getrequestcall(
        GET_CLIENT_COMMUNICATOR_DETAIL +
          `${selectedClientData?.ClientEmployeeId}/${currentTab + 1}/${selectedTagId}/${location?.state?.searchText || ""}`,
        loginData?.accessToken
      );
      if (communicatorResponse.status === 200) {
        if (typeof dataId === "number") {
          const filtered = communicatorResponse?.data?.data.filter((data) => data.id === dataId);
          setShareData(filtered[0] || []);
        }
        setCommunicatorData(communicatorResponse?.data?.data);
      } else {
        setCommunicatorData([]);
      }
    } catch (error) {
      setCommunicatorData([]);
      console.log("getCommunicatorData-> Error Getting Communicator Data: ", error);
    }
    setIsLoading(false);
  };

  const handleShowViewModel = (data) => {
    setViewCommunicator(true);
    setShareData(data);
  };
  const handleHideViewModel = async (loadNew) => {
    if (typeof dataId === "number") {
      navigate("", { replace: true });
    }
    setViewCommunicator(false);
    setShareData([]);
    if (loadNew === true) {
      await getCommunicatorData();
    }
  };
  const CustomListing = ({ data, index }) => {
    return (
      <Card key={index} className="client-card" onClick={() => handleShowViewModel(data)}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar src={data.profilePictureUrl} />
              <Typography variant="body1" style={{ fontWeight: "bold" }}>
                {data.userName}
              </Typography>
            </Stack>
            <Typography variant="body1" style={{ fontWeight: data.acknowledged === 0 ? "bold" : "", color:data.acknowledged === 0 ? "black" : "grey" }}>
              {dayjs(data.date).format("MMMM DD, YYYY")}
            </Typography>
          </Stack>
          <Stack
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 2
            }}>
            <Chip
              sx={{
                borderRadius: "12px"
              }}
              label={data.clientCommunicatorCategory}
            />
            <span style={{ fontWeight: data.acknowledged === 0 ? "bold" : "" }}>
              {data?.subject}
            </span>
          </Stack>
          <Typography variant="body1">{data.body}</Typography>
        </Stack>
      </Card>
    );
  };

  const handleChange = (event) => {
    if (currentTab !== event) {
      setCommunicatorData([]);
      setCurrentTab(event);
    }
  };

  const handleOpenTagFilter = () => {
    setTagFilterOpen(true);
  };

  const handleCloseTagFilter = () => {
    setTagFilterOpen(false);
  };

  const handleSelectTag = (tagId) => {
    setSelectedTagId(tagId);
    setTagFilterOpen(false);
  };

  const handleClearTagFilter = () => {
    setSelectedTagId(0);
    setTagFilterOpen(false);
  };

  const selectedTagLabel =
    selectedTagId === 0
      ? tagLanguage.TEXT_ALL_TAGS
      : employeeTags.find((tag) => tag.value === selectedTagId)?.label || tagLanguage.TEXT_ALL_TAGS;

  const showTagFilterBar = tagLookupLoaded && !isTagFilterLoading && employeeTags.length > 0;

  const Tabs = [
    {
      label: "All",
      value: 0,
      translateLabel: TEXT_ALL
    },
    {
      label: "Acknowledged",
      value: 1,
      translateLabel: TEXT_ACKNOWLEDGED
    },
    {
      label: "To Acknowledge",
      value: 2,
      translateLabel: TEXT_TO_ACKNOWLEDGE
    }
  ];

  return (
    <>
      {showTagFilterBar && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mb: 1.5,
            px: 1.5,
            py: 1.25,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(246,248,252,0.98) 100%)",
            boxShadow: "0 8px 24px rgba(16,24,40,0.06)"
          }}>
          <Stack spacing={0.35} sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
              {tagLanguage.TEXT_SELECTED_TAG}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Chip
                size="small"
                label={selectedTagLabel}
                sx={{
                  maxWidth: "100%",
                  borderRadius: "999px",
                  fontWeight: 600,
                  bgcolor: selectedClientData?.AccentColor || "primary.main",
                  color: "#fff",
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                {employeeTags.length} {employeeTags.length === 1 ? "tag" : "tags"}
              </Typography>
            </Stack>
          </Stack>
          <Button
            variant="outlined"
            onClick={handleOpenTagFilter}
            startIcon={<FilterAltOutlinedIcon />}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 2.25,
              minWidth: 136,
              borderColor: selectedClientData?.AccentColor || "divider",
              color: selectedClientData?.AccentColor || "text.primary",
              "&:hover": {
                borderColor: selectedClientData?.AccentColor || "text.primary",
                backgroundColor: "rgba(0,0,0,0.02)"
              }
            }}>
            {tagLanguage.TEXT_FILTER_BY_TAG}
          </Button>
        </Stack>
      )}
      <TabsComponent
        tabValue={currentTab}
        style={{
          marginBottom: "12px"
        }}
        tabs={Tabs}
        setTabsValue={handleChange}
      />
      {isLoading ? (
        <Loader fullScreen />
      ) : (
        <>
          {!communicatorData || communicatorData.length === 0 ? (
            <Stack sx={{ width: "100%" }} justifyContent="center" alignItems="center" height="85vh">
              <Typography variant="body1" color="grey" align="center" sx={{ mt: 4 }}>
                {TEXT_NO_MASSAGES}
              </Typography>
            </Stack>
          ) : (
            <Stack gap={1}>
              {communicatorData?.map((data, index) => (
                <CustomListing key={index} index={index} data={data} />
              ))}
            </Stack>
          )}
        </>
      )}
      {viewCommunicator && (
        <ClientCommunicatorViewModal
          open={viewCommunicator}
          handleClose={handleHideViewModel}
          shareData={shareData}
        />
      )}
      {showTagFilterBar && (
        <ClientCommunicatorFilterModal
          open={tagFilterOpen}
          onClose={handleCloseTagFilter}
          onSelectTag={handleSelectTag}
          onClearFilter={handleClearTagFilter}
          selectedTagId={selectedTagId}
          tags={employeeTags}
          accentColor={selectedClientData?.AccentColor}
          text={tagLanguage}
        />
      )}
    </>
  );
}

export default Communicator;
